import type { CheckResourcesResponse, ResourceCheck } from '@cerbos/core';

import { canonicalJson } from './canonical-json';

/**
 * Upper bound on the number of distinct decisions kept for a principal.
 * Per-item checks (one `ResourceCheck` per list row) create one entry each, so
 * a long session browsing large lists would otherwise grow this map without
 * limit. Eviction is FIFO on insertion order; evicting a decision that is still
 * on screen only costs one extra request the next time it mounts.
 */
export const MAX_CACHE_ENTRIES = 500;

/**
 * How long a decision may be served before it is asked for again.
 *
 * A principal change and a sign-out both drop the cache, but neither covers an
 * authorization change made behind the app's back — a role or permission
 * revoked server-side reaches this app only on the next token, and not every
 * such change alters a keyed claim. Without an upper bound a decision taken
 * once would then be served for as long as the tab stays open, which is the
 * wrong property for a check that gates what data is rendered.
 *
 * Five minutes keeps the fix this cache exists for (a project switch re-mounts
 * the same checks seconds apart, and still paints from the cache) while
 * bounding how long a revoked permission can survive. The bound is on *serving*
 * a decision: a component that already rendered one keeps it until it asks
 * again, exactly as an uncached check does.
 */
export const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry<TValue> {
  value: TValue;
  /** `Date.now()` past which this decision is no longer served. */
  expiresAt: number;
}

export interface CheckCache<TValue> {
  /**
   * The cached decision for `cacheKey`, or `undefined` on a miss — including
   * when the cache holds decisions for a different principal, and when the
   * decision is older than {@link CACHE_TTL_MS}.
   */
  read: (principalKey: string, cacheKey: string) => TValue | undefined;
  /**
   * Resolves `cacheKey` for `principalKey`, joining the request already in
   * flight for the same key when there is one, and caching a resolved result.
   * A rejection is not cached, so the next caller retries.
   */
  request: (
    principalKey: string,
    cacheKey: string,
    fetcher: () => Promise<TValue>,
  ) => Promise<TValue>;
  /** Drops every cached and in-flight decision. */
  clear: () => void;
}

/**
 * Builds a decision cache holding one principal's answers at a time. Exported
 * for its own unit tests; application code uses {@link checkResourcesCache}.
 */
export const createCheckCache = <TValue>(): CheckCache<TValue> => {
  let currentPrincipalKey: string | undefined;
  let entries = new Map<string, CacheEntry<TValue>>();
  let inFlight = new Map<string, Promise<TValue>>();

  const reset = (principalKey: string | undefined): void => {
    currentPrincipalKey = principalKey;
    entries = new Map<string, CacheEntry<TValue>>();
    inFlight = new Map<string, Promise<TValue>>();
  };

  const store = (cacheKey: string, value: TValue): void => {
    while (entries.size >= MAX_CACHE_ENTRIES) {
      const oldest = entries.keys().next();
      if (oldest.done) break;
      entries.delete(oldest.value);
    }
    entries.set(cacheKey, { value, expiresAt: Date.now() + CACHE_TTL_MS });
  };

  return {
    read: (principalKey, cacheKey) => {
      if (currentPrincipalKey !== principalKey) return undefined;

      const entry = entries.get(cacheKey);
      if (!entry) return undefined;

      if (entry.expiresAt <= Date.now()) {
        // Dropped rather than left to expire again on the next read: the next
        // caller re-requests it, and this keeps the FIFO bound counting live
        // decisions only.
        entries.delete(cacheKey);
        return undefined;
      }

      return entry.value;
    },

    request: async (principalKey, cacheKey, fetcher) => {
      // A different principal invalidates everything: never serve one user's
      // decisions to another, and never let the new principal join a request
      // that was issued for the previous one.
      if (currentPrincipalKey !== principalKey) {
        reset(principalKey);
      }

      const pending = inFlight.get(cacheKey);
      if (pending) return pending;

      const request = fetcher().then((value) => {
        // Drop a response that outlived its principal (a sign-out or tenant
        // switch landed while it was in flight).
        if (currentPrincipalKey === principalKey) {
          store(cacheKey, value);
        }
        return value;
      });

      inFlight.set(cacheKey, request);
      // Separate chain: `request` itself is handed to — and settled by — every
      // caller, which is where a rejection is reported. This one only frees the
      // in-flight slot, and must not leave that rejection unhandled on the way.
      void request
        .catch(() => undefined)
        .finally(() => {
          if (inFlight.get(cacheKey) === request) {
            inFlight.delete(cacheKey);
          }
        });

      return request;
    },

    clear: () => {
      reset(undefined);
    },
  };
};

/**
 * Decisions from `client.checkResources`, keyed by principal and by the
 * serialized batch of checks.
 *
 * Deliberately *not* keyed on the Cerbos client: the app builds one
 * `CerbosClient` against one PDP, so two clients answering the same question
 * differently cannot happen today. Pointing part of the app at a second PDP
 * would make that assumption false — the two would share this map and serve
 * each other's decisions — so a second client has to bring its own cache
 * instance (`createCheckCache`) rather than reuse this one.
 *
 * Going offline is not keyed either: a decision made while online keeps being
 * served, which is the same thing the network stack does with a page that is
 * already loaded, and the alternative is an offline app that silently hides
 * everything permission-gated.
 */
export const checkResourcesCache: CheckCache<CheckResourcesResponse> =
  createCheckCache<CheckResourcesResponse>();

/**
 * Stable cache key for a batch of resource checks.
 *
 * Two call sites asking the same question must produce the same key, and
 * `JSON.stringify` alone does not give that: it is sensitive to property order
 * (`attr` follows the caller's object literal), to `undefined` (an omitted
 * `scope` and an explicit `scope: undefined` serialize differently) and to the
 * order of `actions` and of the checks themselves, none of which changes the
 * decision. A mismatch only costs an extra PDP request, but an invisible one.
 *
 * Both orders are safe to normalise because the cached response is read back
 * by `kind`/`id`/`scope` (`allowedActions`), never positionally.
 */
export const getCheckResourcesKey = (resources: ResourceCheck[]): string =>
  canonicalJson(
    resources
      .map((check) => canonicalJson({ ...check, actions: [...check.actions].sort() }))
      .sort(),
  );

/**
 * Drops every cached Cerbos decision.
 *
 * Decisions are already scoped to the principal that produced them, so this is
 * only needed when the *same* principal may have been re-authorized behind the
 * app's back — i.e. on sign-out, so that a later session for the same user asks
 * the PDP again instead of reusing decisions made before they logged out. Tests
 * call it between cases to keep them independent.
 */
export const clearCerbosCheckCache = (): void => {
  checkResourcesCache.clear();
};
